import { useState } from 'react'
import { formatApiDate } from '~/shared/lib/date'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import BlockIcon from '@mui/icons-material/Block'
import CheckIcon from '@mui/icons-material/Check'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useQueryClient } from '@tanstack/react-query'

import {
  bffCrmOnboardingSignupBlock,
  bffCrmOnboardingReset,
  bffCrmOnboardingDeactivate,
  bffCrmOnboardingTimeline,
} from '@fireflyhealth/api'
import type {
  BffCrmOnboardingSignupBlockRetrieveResponse,
  BffCrmOnboardingResetCreateBodyParams,
} from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import { useMutation } from '~/shared/hooks/useMutation'
import { patientsSlice } from '~/entities/person'
import { SectionCard, StatusPill } from '~/shared/ui'
import { usePatientDetail } from '~/features/member-profile'
import { useAppDispatch } from '~/app/store/hooks'
import { NAVY } from '~/shared/lib/theme'

type CardData = BffCrmOnboardingSignupBlockRetrieveResponse
type AssistOption = NonNullable<CardData['assist_options']>[number]
type AssistAction = NonNullable<AssistOption['action']>

const formatTimestamp = (value?: string | null) =>
  value ? `${formatApiDate(value, 'MMM d, yyyy')} · ${formatApiDate(value, 'h:mm a')}` : null

/** The confirmation dialog, rendered entirely from the server-computed `action.confirm`. */
const ConfirmActionModal = ({
  action,
  loading,
  onConfirm,
  onClose,
}: {
  action: AssistAction
  loading: boolean
  onConfirm: () => void
  onClose: () => void
}) => {
  const { confirm } = action
  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      aria-labelledby="signup-block-confirm-title"
      slotProps={{
        paper: {
          sx: { borderRadius: '12px', width: '460px', maxWidth: 'calc(100vw - 48px)' },
        },
        backdrop: { sx: { backgroundColor: alpha(NAVY[500], 0.5) } },
      }}
    >
      <DialogTitle
        id="signup-block-confirm-title"
        sx={{ fontSize: '1.6rem', fontWeight: 600, color: 'text.primary', px: 3, pt: 3, pb: 1 }}
      >
        {confirm.title}
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25 }}>
          {confirm.body}
        </Typography>
        {confirm.resets.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
            {confirm.resets.map(entry => (
              <Box key={entry} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  aria-hidden
                  sx={theme => ({
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: theme.palette.tones.error.fg,
                    flex: '0 0 auto',
                  })}
                />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {entry}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            bgcolor: theme.palette.tones.info.bg,
            color: theme.palette.tones.info.fg,
            borderRadius: '8px',
            px: 1.5,
            py: 1.25,
          })}
        >
          <InfoOutlinedIcon sx={{ fontSize: '1.6rem', flex: '0 0 auto', mt: '1px' }} />
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            {confirm.info}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pt: 2, pb: 3 }}>
        <Button color="primary" disabled={loading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action.kind === 'deactivate' ? 'error' : 'primary'}
          disabled={loading}
          onClick={onConfirm}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {confirm.confirm_label}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Ops signup-block card on the patient Onboarding tab. Every string, context row, script step and
 * action — including the confirmation-dialog copy — is server-computed (keep the client dumb); this
 * component only renders the payload and fires the reset / deactivate mutations. Renders nothing
 * when the loader returns an empty body (switch OFF, never blocked, or past `INITIALIZED`).
 *
 * The `blocked` card carries the full assist surface — reason pill, what-the-member-entered panel,
 * step-by-step script, chooser (invalid-member-id) and the footer action; the `resolved` card is
 * informational (pill + resolved note only), since the server 400s a reset/deactivate once cleared.
 */
const SignupBlockCard = ({
  personId,
  userId,
}: {
  personId: number
  /** Linked `User.id`, used to refresh the Redux-backed patient the header reads. Null for a
   * person-only view (no `User`), where there is no header patient to refresh. */
  userId?: number | null
}) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { refetch: refetchPatient } = usePatientDetail()

  const { data } = useQuery<CardData>(
    bffCrmOnboardingSignupBlock.retrieveQueryKey({ person_id: personId }),
    () => bffCrmOnboardingSignupBlock.retrieve({ person_id: personId }).then(r => r.data),
    { staleTime: 0, refetchOnMount: 'always', enabled: !!personId },
  )

  const options = data?.assist_options ?? []
  // For invalid_member_id the payload carries two options (wrong-option / typo); the chooser is
  // local state. Falling back to the first option covers single-option reasons with no chooser.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const selectedOption = options.find(option => option.key === selectedKey) ?? options[0] ?? null

  const { run: runAction, loading } = useMutation(
    async (action: AssistAction) => {
      try {
        if (action.kind === 'reset') {
          await bffCrmOnboardingReset.create(
            { person_id: personId },
            {
              data: {
                action_key:
                  action.action_key as BffCrmOnboardingResetCreateBodyParams['action_key'],
              },
            },
          )
        } else {
          await bffCrmOnboardingDeactivate.create({ person_id: personId })
        }
        // Success: refresh the patient header, then dismiss the confirmation modal. The header
        // name/initials read from the Redux-backed patient (`state.patients.byId`), so refetching
        // the onboarding context alone leaves a demographics reset's now-null name on screen —
        // re-fetch the User via getPatient. refetchPatient() still refreshes onboarding/programs.
        if (userId != null) {
          await dispatch(patientsSlice.thunks.getPatient(userId))
        }
        await refetchPatient()
        setConfirmOpen(false)
      } finally {
        // Always re-fetch the card + timeline — even when the action failed — so a
        // block that was already cleared (the reset/deactivate 400 race) self-corrects
        // into the resolved/no-card state instead of stranding a stale, un-retryable
        // card behind the modal. The failure itself still propagates to the error toast.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: bffCrmOnboardingSignupBlock.retrieveQueryKey({ person_id: personId }),
          }),
          queryClient.invalidateQueries({
            queryKey: bffCrmOnboardingTimeline.retrieveQueryKey({ person_id: personId }),
          }),
        ])
      }
    },
    { error: "Couldn't complete that action. Please try again." },
  )

  if (!data?.state) return null

  const isBlocked = data.state === 'blocked'
  const pillTone = isBlocked ? 'error' : 'success'
  const pillLabel = isBlocked ? (data.reason?.display_label ?? 'Signup blocked') : 'Block cleared'
  const timestamp = formatTimestamp(isBlocked ? data.blocked_at : data.resolved_at)
  const accentColor = isBlocked ? theme.palette.tones.error.fg : theme.palette.tones.success.fg
  const showChooser = isBlocked && options.length > 1
  const action = isBlocked ? selectedOption?.action : null

  return (
    <Box sx={{ px: 3, pt: 1.5 }} data-testid="signup-block-card">
      <SectionCard
        accentColor={accentColor}
        footer={
          isBlocked && selectedOption ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', flex: 1, minWidth: '220px' }}
              >
                {selectedOption.footer_note}
              </Typography>
              {action ? (
                <Button variant="contained" color="primary" onClick={() => setConfirmOpen(true)}>
                  {action.label}
                </Button>
              ) : (
                <StatusPill tone="neutral" label="No reset required" />
              )}
            </Box>
          ) : undefined
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary' }}>
                Signup block
              </Typography>
              <StatusPill
                tone={pillTone}
                label={pillLabel}
                icon={isBlocked ? <BlockIcon /> : <CheckIcon />}
              />
            </Box>
            {timestamp && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {isBlocked ? 'Blocked' : 'Resolved'} {timestamp}
              </Typography>
            )}
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '640px' }}>
            {isBlocked ? data.explainer : data.resolved_note}
          </Typography>

          {isBlocked && data.context_rows && data.context_rows.length > 0 && (
            <Box sx={{ bgcolor: 'background.pane', borderRadius: '8px', px: 2, py: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.02em', mb: 1 }}
              >
                What the member entered
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '180px 1fr',
                  columnGap: 2,
                  rowGap: 0.75,
                }}
              >
                {data.context_rows.map(row => (
                  <Box key={row.label} sx={{ display: 'contents' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={theme => ({
                        fontWeight: 500,
                        fontFamily: row.mono ? theme.typography.fontFamilyMono : undefined,
                      })}
                    >
                      {row.value ?? '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {showChooser && (
            <RadioGroup
              value={selectedOption?.key ?? ''}
              onChange={event => setSelectedKey(event.target.value)}
              sx={{ flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1 }}
            >
              {options.map(option => {
                const selected = option.key === (selectedOption?.key ?? '')
                return (
                  <FormControlLabel
                    key={option.key}
                    value={option.key}
                    control={
                      <Radio size="small" sx={{ alignSelf: 'flex-start', py: 0, mt: 0.25 }} />
                    }
                    sx={theme => ({
                      flex: '1 1 260px',
                      alignItems: 'flex-start',
                      m: 0,
                      p: 1.5,
                      borderRadius: '8px',
                      border: `1px solid ${
                        selected ? theme.palette.primary.light : theme.palette.divider
                      }`,
                      bgcolor: selected
                        ? theme.palette.primary.lighter
                        : theme.palette.background.default,
                    })}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {option.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {option.description}
                        </Typography>
                      </Box>
                    }
                  />
                )
              })}
            </RadioGroup>
          )}

          {isBlocked && selectedOption && <SignupBlockScript option={selectedOption} />}
        </Box>
      </SectionCard>

      {action && confirmOpen && (
        <ConfirmActionModal
          action={action}
          loading={loading}
          onConfirm={() => runAction(action)}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </Box>
  )
}

/** The collapsible step-by-step script for the selected assist option, expanded by default. */
const SignupBlockScript = ({ option }: { option: AssistOption }) => {
  const [open, setOpen] = useState(true)
  if (option.script_steps.length === 0) return null
  return (
    <Box sx={theme => ({ borderTop: `1px solid ${theme.palette.divider}`, pt: 1.25 })}>
      <Button
        variant="text"
        color="inherit"
        onClick={() => setOpen(prev => !prev)}
        startIcon={
          <KeyboardArrowDownIcon
            sx={{
              color: 'text.secondary',
              transition: 'transform 150ms ease',
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          />
        }
        sx={{
          p: 0,
          minWidth: 0,
          justifyContent: 'flex-start',
          fontSize: '1.3rem',
          fontWeight: 600,
          color: 'text.primary',
          '&:hover': { bgcolor: 'transparent' },
        }}
      >
        Step-by-step script for assisting the member
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.25 }}>
          {option.script_steps.map((step, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                aria-hidden
                sx={theme => ({
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  flex: '0 0 auto',
                  mt: '1px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  bgcolor: theme.palette.tones.info.bg,
                  color: theme.palette.tones.info.fg,
                })}
              >
                {index + 1}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                {step}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

export default SignupBlockCard
